"use client";

import React, { useRef } from "react";
import { AnimatedBeam } from "@/components/magicui/animated-beam";
import { cn } from "@/lib/utils";
import { Brain, Sparkles, Target, TrendingUp } from "lucide-react";
import { QuiltLogo } from "./QuiltLogo";
import ShinyText from "./reactbits/ShinyText";

// Modified to accept props for TooltipTrigger and use previous styling (rounded-2xl, specific colors)
const IconNode = React.forwardRef<
    HTMLDivElement,
    { className?: string; children?: React.ReactNode } & React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
    return (
        <div
            ref={ref}
            className={cn(
                "z-10 flex h-20 w-20 items-center justify-center rounded-full border-2 bg-white shadow-sm transition-transform hover:scale-105 active:scale-95 cursor-pointer",
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
});

IconNode.displayName = "IconNode";

export function SolutionLoop({ className, onStepClick }: { className?: string; onStepClick?: (time: number) => void }) {
    const containerRef = useRef<HTMLDivElement>(null);
    const predictRef = useRef<HTMLDivElement>(null);
    const generateRef = useRef<HTMLDivElement>(null);
    const serveRef = useRef<HTMLDivElement>(null);
    const optimizeRef = useRef<HTMLDivElement>(null);

    return (
        <div
            className={cn(
                "relative flex min-h-[600px] w-full items-center justify-center",
                className,
            )}
            ref={containerRef}
        >
            <div className="flex size-full max-w-lg flex-col items-stretch justify-between gap-20">

                {/* Top: Predict */}
                <div className="flex flex-col items-center justify-center gap-2 text-center relative">
                    <IconNode
                        ref={predictRef}
                        className="border-fuchsia-200 bg-fuchsia-50 text-fuchsia-500 dark:border-fuchsia-800 dark:bg-fuchsia-950/30 backdrop-blur-sm"
                        onClick={() => onStepClick?.(6)}
                    >
                        <Brain className="h-8 w-8" />
                    </IconNode>
                    <ShinyText text="PREDICT" className="tracking-[0.1rem] " />
                    <p className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-max text-xs text-slate-900 dark:text-white/35 italic font-thin max-w-[240px]">Identify which content features correlate with a specific patient's engagement</p>
                </div>

                {/* Middle Row: Optimize (Left) - Center Text - Generate (Right) */}
                <div className="flex flex-row items-center justify-between">

                    {/* Left: Optimize */}
                    <div className="flex flex-col items-center justify-center gap-2 text-center relative">
                        <IconNode
                            ref={optimizeRef}
                            className="border-cyan-200 bg-cyan-50 text-cyan-500 dark:border-cyan-800 dark:bg-cyan-950/30 backdrop-blur-sm"
                            onClick={() => onStepClick?.(4)}
                        >
                            <TrendingUp className="h-8 w-8" />
                        </IconNode>
                        <ShinyText text="OPTIMIZE" className="tracking-[0.1rem] " />
                        <p className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-max text-xs text-slate-900 dark:text-white/35 italic font-thin max-w-[200px]">Every interaction feeds back into the engine, making the next piece of content more effective.</p>
                    </div>

                    {/* Center Text */}
                    <div className="z-10 flex flex-col items-center justify-center text-center p-6 rounded-full backdrop-blur-xl w-40 h-40">
                        <QuiltLogo alt="Quilt Logo" className="w-12 h-12 mb-2" />
                        <ShinyText text="Predictive Engine" />
                    </div>

                    {/* Right: Generate */}
                    <div className="flex flex-col items-center justify-center gap-2 text-center relative">
                        <IconNode
                            ref={generateRef}
                            className="border-purple-200 bg-purple-50 text-purple-500 dark:border-purple-800 dark:bg-purple-950/30 backdrop-blur-sm"
                            onClick={() => onStepClick?.(10)}
                        >
                            <Sparkles className="h-8 w-8" />
                        </IconNode>
                        <ShinyText text="GENERATE" className="tracking-[0.1rem] " />
                        <p className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-max text-xs text-slate-900 dark:text-white/35 italic font-thin max-w-[200px]">Using Generative AI, Quilt creates bespoke wellness content tailored to predictions.</p>
                    </div>
                </div>

                {/* Bottom: Serve */}
                <div className="flex flex-col items-center justify-center gap-2 text-center relative">
                    <IconNode
                        ref={serveRef}
                        className="border-blue-200 bg-blue-50 text-blue-500 dark:border-blue-800 dark:bg-blue-950/30 backdrop-blur-sm"
                        onClick={() => onStepClick?.(23)}
                    >
                        <Target className="h-8 w-8" />
                    </IconNode>
                    <ShinyText text="SERVE" className="tracking-[0.1rem] " />
                    <p className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-max text-xs text-slate-900 dark:text-white/35 italic font-thin max-w-[200px]">Content is delivered via the provider portal or Quilt app after clinic sign-off.</p>
                </div>
            </div>

            {/* Beams connecting the circle */}
            {/* Predict (Top) -> Generate (Right) */}
            <AnimatedBeam
                containerRef={containerRef}
                fromRef={predictRef}
                toRef={generateRef}
                curvature={120}
                // startXOffset={39}
                // endYOffset={-39}
                trajectory="circle"
            />

            {/* Generate (Right) -> Serve (Bottom) */}
            <AnimatedBeam
                containerRef={containerRef}
                fromRef={generateRef}
                toRef={serveRef}
                curvature={-120}
                // endXOffset={39}
                // endYOffset={-39}
                reverse={true}
                trajectory="circle"
            />

            {/* Serve (Bottom) -> Optimize (Left) */}
            <AnimatedBeam
                containerRef={containerRef}
                fromRef={serveRef}
                toRef={optimizeRef}
                curvature={-120}
                endYOffset={0}
                reverse={true}
                trajectory="circle"
            />

            {/* Optimize (Left) -> Predict (Top) */}
            <AnimatedBeam
                containerRef={containerRef}
                fromRef={optimizeRef}
                toRef={predictRef}
                curvature={120}
                reverse={false}
                trajectory="circle"

            />

        </div>
    );
}
